/**
 * Okta Authentication Service - WEB
 *
 * Web-specific implementation using HTTP-only session cookies.
 * No token storage needed - the browser automatically sends cookies.
 *
 * Security:
 * - Session cookies are HTTP-only (not accessible to JavaScript)
 * - Roles come from server (validated on every request)
 * - No client-side token management
 */

import { fetchAuthStatusHttp } from './fetchAuthStatus';
import { authStatusFromHttp, mapAuthStatusUser } from './mapAuthStatusUser';
import { reportSkippedOktaLogoutRedirect } from './reportSkippedOktaLogoutRedirect';
import { getApiConfig, isWebpackDevLocalWebOrigin } from '../../config/api';
import { AuthStatusResult, IAuthService, User } from '../../types/auth';
import { isValidOktaRedirectUrl } from '../../utils/urlValidation';
import { BIND_AFTER_LOGIN_FLAG } from '../bindDomainMembershipKeys';
import { storage } from '../storage';

/** Same-origin base (empty) when using webpack dev proxy; otherwise host from env. */
function webApiRoot(): string {
  return getApiConfig().BASE_URL.replace(/\/$/, '');
}

function webApiUrl(path: string): string {
  const root = webApiRoot();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${root}${normalized}`;
}

function assertWebApiConfigured(): void {
  const base = getApiConfig().BASE_URL;
  if (base === '' && isWebpackDevLocalWebOrigin()) {
    return;
  }
  if (!base) {
    throw new Error(
      `❌ API_BASE_URL not configured for Okta web authentication\n` +
        `Please set one of the following in your .env file:\n` +
        `  - REACT_APP_API_BASE_URL_WEB\n` +
        `  - REACT_APP_API_BASE_URL\n\n` +
        `Example:\n` +
        `  REACT_APP_API_BASE_URL_WEB=https://itsapiqa.vertiv.com\n\n` +
        `For local development with yarn web + yarn server, open http://localhost:3000 (webpack proxies /api to the API).`,
    );
  }
}

export class OktaAuthServiceWeb implements IAuthService {
  private currentUser: User | null = null;
  private lastSessionCheck: number = 0;
  private readonly SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

  async login(): Promise<User> {
    if (__DEV__) {
      console.log('🌐 Okta (web): Initiating OAuth login');
    }

    try {
      assertWebApiConfigured();

      // Best Practice: Fetch the OAuth URL from server, then redirect manually
      // This avoids IIS rewriting the server's redirect to Okta
      const loginUrl = webApiUrl('/api/auth/login?platform=web');

      const response = await fetch(loginUrl, {
        method: 'GET',
        credentials: 'include', // Include session cookie
        headers: {
          Accept: 'application/json',
        },
      });

      const raw = await response.text();
      let data: { success?: boolean; redirectUrl?: string; error?: string } =
        {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }

      if (!response.ok) {
        const serverHint =
          typeof data.error === 'string' && data.error.length > 0
            ? `: ${data.error}`
            : '';
        throw new Error(`Login failed (${response.status})${serverHint}`);
      }

      if (data.success && data.redirectUrl) {
        // Validate against the expected Okta domain before navigating away (CWE-601:
        // never trust a redirect target from a network response without checking it).
        if (
          !isValidOktaRedirectUrl(
            data.redirectUrl,
            process.env.REACT_APP_OKTA_DOMAIN || '',
          )
        ) {
          throw new Error(
            'Login failed: server returned an unexpected redirect target',
          );
        }

        if (__DEV__) {
          console.log(
            '🌐 Okta (web): Redirecting to Okta authorization endpoint',
          );
        }

        // Redirect browser to Okta (client-side redirect, IIS cannot interfere)
        window.location.href = data.redirectUrl;
      } else {
        throw new Error(
          typeof data.error === 'string' && data.error.length > 0
            ? data.error
            : 'No redirect URL received from server',
        );
      }
    } catch (error) {
      console.error('❌ Okta (web): Login initiation error:', error);

      // Parse error for user-friendly messaging
      let userMessage = 'Failed to start authentication. Please try again.';

      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();

        if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          userMessage =
            'Network error. Please check your connection and try again.';
        } else if (
          errorMsg.includes('configuration') ||
          errorMsg.includes('not configured')
        ) {
          userMessage =
            'Authentication service is not configured. Please contact support.';
        } else if (error.message.startsWith('Login failed (')) {
          // Surface server/Okta errors (e.g. missing env, OIDC discovery failure)
          userMessage = error.message;
        } else if (
          error.message !== 'No redirect URL received from server' &&
          error.message.length < 220
        ) {
          userMessage = error.message;
        }
      }

      throw new Error(userMessage);
    }

    // This promise will never resolve as we're redirecting
    return new Promise(() => {});
  }

  async logout(): Promise<void> {
    try {
      if (__DEV__) {
        console.log('🌐 Okta (web): Logging out');
      }

      // Clear in-memory cache and local storage first
      this.currentUser = null;
      await storage.removeItem('oktaAuthUser');
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(BIND_AFTER_LOGIN_FLAG);
        }
      } catch {
        // Marker is session-only; a blocked remove must not fail logout.
      }
      // Call server logout endpoint
      const response = await fetch(webApiUrl('/api/auth/logout'), {
        method: 'POST',
        credentials: 'include', // Send HTTP-only cookies
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('⚠️ Server logout failed, but local logout completed');
        return;
      }

      // Parse logout response
      try {
        const logoutResponse = await response.json();

        if (
          logoutResponse.success &&
          logoutResponse.redirectUrl &&
          isValidOktaRedirectUrl(
            logoutResponse.redirectUrl,
            process.env.REACT_APP_OKTA_DOMAIN || '',
          )
        ) {
          if (__DEV__) {
            console.log('🌐 Okta (web): Redirecting to Okta logout endpoint');
            console.log('   This will clear your Okta session');
          }

          // Redirect to Okta logout - this is CRITICAL for proper logout
          // Without this, the user's Okta session remains active
          window.location.href = logoutResponse.redirectUrl;

          // Note: Code after this won't execute as we're redirecting
        } else if (logoutResponse.success && logoutResponse.redirectUrl) {
          // Validation failed (CWE-601): skip the redirect rather than navigate to an
          // untrusted target. Local logout already completed above.
          let rejectedHost = 'unparseable';
          try {
            rejectedHost = new URL(logoutResponse.redirectUrl).hostname;
          } catch {
            // keep unparseable
          }
          reportSkippedOktaLogoutRedirect(
            rejectedHost,
            process.env.REACT_APP_OKTA_DOMAIN || '',
          );
        } else {
          if (__DEV__) {
            console.log('✅ Okta (web): Logout completed (local only)');
          }
          if (!logoutResponse.redirectUrl) {
            console.warn(
              '⚠️ No Okta logout URL received - Okta session may still be active',
            );
          }
        }
      } catch (parseError) {
        console.error('❌ Failed to parse logout response:', parseError);
        console.warn(
          '⚠️ Local logout completed, but Okta session may still be active',
        );
      }
    } catch (error) {
      console.error('❌ Okta (web): Logout error:', error);
      this.currentUser = null;
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(BIND_AFTER_LOGIN_FLAG);
        }
      } catch {
        // Marker is session-only; a blocked remove must not fail logout.
      }
      throw error; // Re-throw to allow error handling in AuthContext
    }
  }

  clearUserCache(): void {
    if (__DEV__) {
      console.log('🧹 Okta (web): Clearing user cache');
    }
    this.currentUser = null;
    this.lastSessionCheck = 0;
    storage.removeItem('oktaAuthUser');
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const now = Date.now();
      const timeSinceLastCheck = now - this.lastSessionCheck;
      const shouldValidateSession =
        timeSinceLastCheck > this.SESSION_CHECK_INTERVAL;

      // 1. Check in-memory cache first
      if (this.currentUser) {
        if (__DEV__) {
          console.log('🌐 Okta (web): Found cached user in memory');
        }

        // Periodically validate session to ensure it's still valid
        if (shouldValidateSession) {
          if (__DEV__) {
            console.log(
              '🔍 Okta (web): Session check interval exceeded - validating with server',
            );
          }

          try {
            const response = await fetch(webApiUrl('/api/auth/status'), {
              method: 'GET',
              credentials: 'include',
              headers: {
                Accept: 'application/json',
              },
            });

            this.lastSessionCheck = now;

            if (!response.ok) {
              if (response.status === 401) {
                console.warn(
                  '🔒 Okta (web): Session expired - clearing cached user',
                );
                this.currentUser = null;
                await storage.removeItem('oktaAuthUser');
                return null;
              }
              // For other errors, continue with cached user
              console.warn(
                '⚠️ Okta (web): Session validation failed with status:',
                response.status,
              );
            } else {
              const authStatus = await response.json();
              if (!authStatus.success || !authStatus.isAuthenticated) {
                console.warn(
                  '🔒 Okta (web): Session no longer authenticated - clearing cached user',
                );
                this.currentUser = null;
                await storage.removeItem('oktaAuthUser');
                return null;
              }
              // Update cached user from server response to catch identity changes (e.g., impersonation swap, role changes)
              if (authStatus.user) {
                const updatedUser = mapAuthStatusUser(authStatus.user);
                if (updatedUser) {
                  this.currentUser = updatedUser;
                  await storage.setJSON('oktaAuthUser', updatedUser);
                }
                if (__DEV__) {
                  console.log(
                    '✅ Okta (web): Session validated - cached user updated from server',
                  );
                }
              } else {
                if (__DEV__) {
                  console.log(
                    '✅ Okta (web): Session validated - still active',
                  );
                }
              }
            }
          } catch (validationError) {
            console.warn(
              '⚠️ Okta (web): Could not validate session (network error):',
              validationError,
            );

            // Check if browser is actually offline (Wi-Fi/network disconnected)
            const browserIsOffline =
              typeof navigator !== 'undefined' && !navigator.onLine;

            if (browserIsOffline) {
              if (__DEV__) {
                console.log(
                  '📡 Okta (web): Browser reports offline - allowing cached user for offline work',
                );
              }
              // Continue with cached user on network errors (offline mode)
            } else {
              // Browser thinks we're online but server is unreachable
              console.warn(
                '❌ Okta (web): Server unreachable (browser online but fetch failed) - clearing session',
              );
              this.currentUser = null;
              await storage.removeItem('oktaAuthUser');
              return null;
            }
          }
        }

        return this.currentUser;
      }

      // 2. Check local storage (works offline)
      const cachedUser = await storage.getJSON<User>('oktaAuthUser');
      if (cachedUser) {
        if (__DEV__) {
          console.log(
            '🌐 Okta (web): Found user in storage - validating session',
          );
        }

        // Always validate session when loading from storage
        try {
          const response = await fetch(webApiUrl('/api/auth/status'), {
            method: 'GET',
            credentials: 'include',
            headers: {
              Accept: 'application/json',
            },
          });

          this.lastSessionCheck = now;

          if (!response.ok) {
            if (response.status === 401) {
              console.warn(
                '🔒 Okta (web): Session expired - clearing stored user',
              );
              await storage.removeItem('oktaAuthUser');
              return null;
            }
            throw new Error(`Status check failed: ${response.status}`);
          }

          const authStatus = await response.json();

          if (
            authStatus.success &&
            authStatus.isAuthenticated &&
            authStatus.user
          ) {
            // Use fresh server data instead of potentially stale storage data
            const freshUser = mapAuthStatusUser(authStatus.user);
            if (!freshUser) {
              await storage.removeItem('oktaAuthUser');
              return null;
            }
            this.currentUser = freshUser;
            await storage.setJSON('oktaAuthUser', freshUser);
            if (__DEV__) {
              console.log(
                '✅ Okta (web): Session valid - user updated from server',
              );
            }
            return freshUser;
          }

          console.warn('🔒 Okta (web): Session no longer authenticated');
          await storage.removeItem('oktaAuthUser');
          return null;
        } catch (validationError) {
          console.warn(
            '⚠️ Okta (web): Could not validate session (network error):',
            validationError,
          );

          // Check if browser is actually offline (Wi-Fi/network disconnected)
          const browserIsOffline =
            typeof navigator !== 'undefined' && !navigator.onLine;

          if (browserIsOffline) {
            if (__DEV__) {
              console.log(
                '📡 Okta (web): Browser reports offline - allowing cached user for offline work',
              );
            }
            // Return cached user on network errors (offline mode)
            this.currentUser = cachedUser;
            return cachedUser;
          } else {
            // Browser thinks we're online but server is unreachable
            console.warn(
              '❌ Okta (web): Server unreachable (browser online but fetch failed) - clearing session',
            );
            await storage.removeItem('oktaAuthUser');
            return null;
          }
        }
      }

      // 3. No cached user - validate with server
      if (__DEV__) {
        console.log('🌐 Okta (web): No cached user - checking server session');
      }

      const response = await fetch(webApiUrl('/api/auth/status'), {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });

      this.lastSessionCheck = now;

      if (!response.ok) {
        if (response.status === 401) {
          if (__DEV__) {
            console.log('🌐 Okta (web): No valid session');
          }
          this.currentUser = null;
          return null;
        }
        throw new Error(`Status check failed: ${response.status}`);
      }

      const authStatus = await response.json();

      if (authStatus.success && authStatus.isAuthenticated && authStatus.user) {
        const user = mapAuthStatusUser(authStatus.user);
        if (!user) {
          this.currentUser = null;
          return null;
        }

        this.currentUser = user;
        await storage.setJSON('oktaAuthUser', user);
        if (__DEV__) {
          console.log('✅ Okta (web): Valid session, user cached');
        }
        return user;
      }

      this.currentUser = null;
      return null;
    } catch (error) {
      console.error('❌ Okta (web): getCurrentUser error:', error);

      // If we have cached data, use it (offline fallback)
      const cachedUser = await storage.getJSON<User>('oktaAuthUser');
      if (cachedUser) {
        console.warn(
          '⚠️ Okta (web): Using cached user due to error (offline mode)',
        );
        this.currentUser = cachedUser;
        return cachedUser;
      }

      this.currentUser = null;
      return null;
    }
  }

  async getAuthStatus(): Promise<AuthStatusResult> {
    try {
      const http = await fetchAuthStatusHttp(webApiUrl('/api/auth/status'), {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });
      return authStatusFromHttp(http);
    } catch {
      return { kind: 'error' };
    }
  }

  async refreshToken(): Promise<string> {
    try {
      if (__DEV__) {
        console.log('🌐 Okta (web): Refreshing token');
      }

      const response = await fetch(webApiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();
      return data.token || '';
    } catch (error) {
      console.error('❌ Okta (web): Token refresh error:', error);
      throw new Error(
        `Token refresh failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  /**
   * Handle OAuth callback (for web platforms)
   */
  async handleAuthCallback(): Promise<User | null> {
    try {
      if (__DEV__) {
        console.log('🌐 Okta (web): Handling OAuth callback');
      }

      // Server has already processed the callback and set the session cookie
      const user = await this.getCurrentUser();

      if (user) {
        if (__DEV__) {
          console.log('✅ Okta (web): Authentication successful');
        }
        return user;
      }

      throw new Error('Authentication callback failed - no user data received');
    } catch (error) {
      console.error('❌ Okta (web): Callback error:', error);
      throw error;
    }
  }

  /**
   * Check if we're in the middle of an OAuth callback flow
   */
  isAuthCallback(): boolean {
    if (typeof window === 'undefined') return false;

    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') && urlParams.has('state');
  }

  /**
   * Get the authentication URL
   */
  getAuthUrl(): string {
    return webApiUrl('/api/auth/login');
  }

  /**
   * Web uses HTTP-only cookies, no client-accessible token
   */
  async getToken(): Promise<string | null> {
    return null;
  }
}

// Export singleton instance
export const oktaAuthService = new OktaAuthServiceWeb();
